import * as Logger from "js-logger";
import { I18NConfigSchema } from "slotworks/config/i18n-config-schema";
import { lowerCaseMapKeys, lowerCaseMapValues } from "slotworks/utils/collection-utils";
import { parseJSONMap } from "slotworks/utils/json-utils";

class Translations {

    public debug: boolean = false;

    public formats: { [id: string]: { [id: string]: any } };

    public breakWords: boolean;

    public code: string;

    private breakWordLanguages: string[] = ["zh", "ja", "th", "ko", "test"];

    private pixiToCSS: { [id: string]: string } = {
        fontFamily: "font-family",
        fill: "color",
        fontSize: "font-size"
    };

    private aliases: Map<string, string>;

    private translations: Map<string, string>;

    public setLanguage(code: string) {
        this.breakWords = false;

        for (const breakWordLanguage of this.breakWordLanguages) {
            if (code.toLocaleLowerCase().indexOf(breakWordLanguage) > -1) {
                this.breakWords = true;
            }
        }

        this.code = code;
    }

    public setConfig(config: I18NConfigSchema) {
        this.aliases = parseJSONMap(config.aliases);

        lowerCaseMapKeys(this.aliases);
        lowerCaseMapValues(this.aliases);

        if (config.formats) {
            this.formats = config.formats;
        } else {
            this.formats = {};
        }
    }

    public load(json: any) {
        this.translations = parseJSONMap<string>(json.translations);

        lowerCaseMapKeys(this.translations);

        if (json.formats) {
            for (const format in json.formats) {
                if (json.formats.hasOwnProperty(format)) {
                    this.formats[format] = json.formats[format];
                }
            }
        }

        if (this.debug) {
            this.aliases.forEach((alias: string) => {
                this.get(alias);
            });
        }
    }

    public set(id: string, translation: string) {
        this.translations.set(id, translation);
    }

    public get(id: string, parameters?: { [value: string]: string | number }): string {
        let translationId = id;

        if (!translationId) {
            Logger.warn("Trying to load an undefined translation ID.");
            return translationId;
        }

        if (!this.aliases) {
            Logger.warn("Trying to use a translation before they were loaded: " + translationId);
            return translationId;
        }

        translationId = translationId.toLowerCase();

        const alias = translationId;

        if (this.aliases.has(translationId)) {
            translationId = this.aliases.get(translationId);
        }

        if (!this.translations.has(translationId)) {
            Logger.warn("Missing translation: " + id);

            if (translationId === "") {
                return "";
            }
            return id;
        }

        const rawTranslation: string = this.translations.get(translationId);

        let translation = "";

        // Find brace blocks
        let searchIndex = -1;

        while (true) {

            const blockIndex = rawTranslation.indexOf("{", searchIndex + 1);
            const endBlockIndex = rawTranslation.indexOf("}", blockIndex + 1);

            if (blockIndex === -1) {
                translation += rawTranslation.slice(searchIndex + 1);
                break;
            }

            translation += rawTranslation.slice(searchIndex + 1, blockIndex);

            const block = rawTranslation.slice(blockIndex, endBlockIndex + 1).split(" ").join("");

            // Skip variable substitution if parameters are not set
            if (parameters) {
                // Find plurals
                if (block.slice(1, 7) === "plural") {
                    const argId = block.slice(8, block.indexOf(")"));
                    const arg = parameters[argId];

                    if (arg !== 1) {
                        translation += block.slice(block.indexOf("?") + 1, block.indexOf(":"));
                    } else {
                        translation += block.slice(block.indexOf(":") + 1, block.indexOf("}"));
                    }
                } else {
                    Object.keys(parameters).forEach((key) => {
                        parameters[key.toLowerCase()] = parameters[key];
                    });

                    // Variable substitution
                    const argId = block.slice(1, block.length - 1).toLowerCase();
                    let arg: string | number = "";
                    if (parameters && parameters[argId] !== undefined) {
                        arg = parameters[argId];
                    }

                    translation += arg;
                }
            }

            searchIndex = endBlockIndex;
        }

        if (this.debug) {
            translation = "$" + translation + "$";
        }

        if (this.formats && this.formats[translationId]) {
            translation = `<${translationId}>${translation}</${translationId}>`;
        } else if (this.aliases && this.formats[alias]) {
            translation = `<${alias}>${translation}</${alias}>`;
        }

        return translation;
    }

    public updateDOMTexts() {
        const domTexts = document.querySelectorAll(".i18n");
        if (domTexts) {
            for (let i = 0; i < domTexts.length; i++) {
                const domElement = domTexts.item(i) as HTMLElement;
                domElement.innerHTML = this.convertTags(this.get(domElement.innerText));
            }
        }
    }

    /**
     * Converts <orange></orange>
     * where orange is defined as
     * {
     *  "fill": "#ff0000",
     *  "fontSize": 100,
     *  "fontFamily": "Arial"
     * }
     *  to <span style="color: #ff0000; font-size: 100%; font-family: Arial"></span>
     * @param text {string}
     */
    public convertTags(text: string): string {
        for (const formatName in this.formats) {
            if (this.formats.hasOwnProperty(formatName)) {
                const format = this.formats[formatName];

                let styleString = "";
                for (const prop in format) {
                    if (format.hasOwnProperty(prop)) {
                        let propName = prop;
                        if (this.pixiToCSS[prop]) {
                            propName = this.pixiToCSS[prop];
                        }
                        let units = "";
                        if (propName === "font-size") {
                            units = "%";
                        }

                        styleString += propName + ": " + format[prop] + units + ";";
                    }
                }

                while (text.indexOf("<" + formatName + ">") > -1) {
                    const tagStart = text.indexOf("<" + formatName + ">");
                    const tagContentsStart = tagStart + formatName.length + 2;
                    const tagContentsEnd = text.indexOf("</" + formatName + ">", tagContentsStart);
                    const tagEnd = tagContentsEnd + formatName.length + 3;

                    const tagContents = text.slice(tagContentsStart, tagContentsEnd);

                    text = text.substring(0, tagStart) + "<span style=\"" + styleString + "\">" + tagContents + "</span>" + text.substr(tagEnd);
                }
            }
        }

        return text;
    }
}

export default new Translations();



// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./~/slotworks/i18n/translations.ts